import { useQuery } from '@apollo/client';
import type {
  OptimizerRunV2State,
  OptimizerRunV2Type,
} from '@generated/graphql';
import { BULK_REBALANCE_ORG_MODE } from '@ovComponents/4-module/bulkRebalance/constants/policy';
import { bulkRebalanceDetailPath } from '@ovComponents/4-module/bulkRebalance/paths';
import { FETCH_OPTIMIZER_RUNS_V2 } from '@ovComponents/4-module/bulkRebalance/queries/optimizerRunsV2.queries';
import type { BulkRebalanceRun } from '@ovComponents/4-module/bulkRebalance/types/run.types';
import { UserContext } from '@providers/userContextProvider';
import { toHoldingServiceSortDesc } from '@utils/holdingServiceSort';
import { usePageState } from '@utils/usePageState';
import type { TFunction } from 'i18next';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { usePermission } from '../../../../hooks/usePermission';
import type { BulkRebalanceListFilters } from '../list/filters';

const DEFAULT_PAGE_SIZE = 15;

export type UseBulkRebalanceListPageResult = {
  t: TFunction;
  canWriteOptimizer: boolean;
  isStartRunModalOpen: boolean;
  openStartRunModal: () => void;
  closeStartRunModal: () => void;
  filters: BulkRebalanceListFilters;
  setTypeFilter: (value?: OptimizerRunV2Type) => void;
  setStateFilter: (value?: OptimizerRunV2State) => void;
  runs: BulkRebalanceRun[];
  loading: boolean;
  errorMessage?: string;
  page: number;
  pageSize: number;
  totalCount: number;
  setPage: (page: number) => void;
  onRunSelect: (runId: string) => void;
};

export const useBulkRebalanceListPage = (): UseBulkRebalanceListPageResult => {
  const { t } = useTranslation(['bulkRebalance']);
  const navigate = useNavigate();
  const { activeOrganization } = useContext(UserContext);
  const canWriteOptimizer = usePermission('write:portfolio_optimizer');
  const [page, setPage] = usePageState(1, 'page');
  const [typeFilter, setTypeFilterState] = usePageState<
    OptimizerRunV2Type | undefined
  >(undefined, 'tp');
  const [stateFilter, setStateFilterState] = usePageState<
    OptimizerRunV2State | undefined
  >(undefined, 'st');
  const [isStartRunModalOpen, setIsStartRunModalOpen] = useState(false);

  const { loading, error, data, previousData } = useQuery(
    FETCH_OPTIMIZER_RUNS_V2,
    {
      fetchPolicy: 'no-cache',
      variables: {
        input: {
          filter: {
            organizationId: activeOrganization.id,
            mode: BULK_REBALANCE_ORG_MODE,
            ...(typeFilter ? { type: typeFilter } : {}),
            ...(stateFilter ? { state: stateFilter } : {}),
          },
          pagination: {
            sortField: 'createdAt',
            sortDesc: toHoldingServiceSortDesc(true),
            offSet: (page - 1) * DEFAULT_PAGE_SIZE,
            perPage: DEFAULT_PAGE_SIZE,
          },
        },
      },
    },
  );

  const queryResult = data ?? previousData;
  const runs = useMemo(
    () => queryResult?.fetchOptimizerRunsV2?.optimizerRuns ?? [],
    [queryResult],
  );
  const totalCount = queryResult?.fetchOptimizerRunsV2?.totalCount ?? 0;

  const setTypeFilter = (value?: OptimizerRunV2Type) => {
    setPage(1);
    setTypeFilterState(value);
  };

  const setStateFilter = (value?: OptimizerRunV2State) => {
    setPage(1);
    setStateFilterState(value);
  };

  return {
    t,
    canWriteOptimizer,
    isStartRunModalOpen,
    openStartRunModal: () => setIsStartRunModalOpen(true),
    closeStartRunModal: () => setIsStartRunModalOpen(false),
    filters: {
      type: typeFilter,
      state: stateFilter,
    },
    setTypeFilter,
    setStateFilter,
    runs,
    loading,
    errorMessage: error?.message,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    totalCount,
    setPage,
    onRunSelect: (runId) => navigate(bulkRebalanceDetailPath(runId)),
  };
};
